import classNames from 'classnames'
import moment from 'moment'
import {
  ChangeEvent,
  CSSProperties,
  FC,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen } from '@fortawesome/free-solid-svg-icons'
import { CustomCalendar } from 'src/App/AppRouter/Layout/TopBar/TopBarDatePicker/CustomCalendar'
import { ReactComponent as CalendarIcon } from 'src/assets/svg/calendar-outline.svg'
import { ReactComponent as ReportIcon } from 'src/assets/svg/summary-report.svg'
import { CampaignSourceSelector } from 'src/components/CampaignSourceSelector'
import { CampaignsSummary } from 'src/components/CampaignSourceSelector/CampaignsSummary'
import { CampaignItem } from 'src/components/CampaignSourceSelector/interfaces/campaignSource.interface'
import { useCampaignSources } from 'src/components/CampaignSourceSelector/useCampaignSources'
import { StatusModal } from 'src/components/StatusModal'
import { BASE_API_DATE_FORMAT } from 'src/constants/dateFormats'
import { useCreateCustomReportMutation } from 'src/reactQuery/useCustomReportMutations'
import { CustomReportModules } from 'src/services/customReportsApi/interfaces/customReports.interface'
import { SupportContactModal } from 'src/pages/AIAssistant/SupportContactModal'
import { BaseModal } from 'src/containers/BaseModal'
import { CreateProjectModalFooter } from 'src/components/ProjectsWidget/CreateProjectModal/CreateProjectModalFooter'
import { CreateProjectModalHeader } from 'src/components/ProjectsWidget/CreateProjectModal/CreateProjectModalHeader'
import styles from './generateReportModal.module.scss'

const TITLE_MAX_LENGTH = 200
const TOTAL_STEPS = 3

type PeriodId = 'custom' | 'last1' | 'last30' | 'last7'

// Preset ranges are expressed in days back from today; 'custom' reveals the
// From/To fields instead.
const PERIOD_OPTIONS: { days?: number; id: PeriodId; label: string }[] = [
  { days: 1, id: 'last1', label: 'Last 1 days' },
  { days: 7, id: 'last7', label: 'Last 7 days' },
  { days: 30, id: 'last30', label: 'Last 30 days' },
  { id: 'custom', label: 'Custom range' },
]

// Roughly the calendar's rendered height; used only to decide whether it fits
// below the field before flipping above it.
const CALENDAR_HEIGHT = 330
const CALENDAR_GAP = 8

// The calendar is portalled to the body rather than positioned inside the
// modal: .modalCard sets overflow:hidden, which clipped the panel, and the
// From/To row is the last thing before the footer so there is no room to open
// upward inside the card either. Fixed positioning against the trigger's rect
// escapes both, and flips above only when there is genuinely no room below.
const CalendarPopover: FC<{
  anchorRef: RefObject<HTMLElement>
  children: ReactNode
}> = ({ anchorRef, children }) => {
  const rect = anchorRef.current?.getBoundingClientRect()
  if (!rect) return null

  const fitsBelow =
    rect.bottom + CALENDAR_GAP + CALENDAR_HEIGHT <= window.innerHeight
  const style: CSSProperties = fitsBelow
    ? { left: rect.left, position: 'fixed', top: rect.bottom + CALENDAR_GAP }
    : {
        bottom: window.innerHeight - rect.top + CALENDAR_GAP,
        left: rect.left,
        position: 'fixed',
      }

  return createPortal(
    <div className={styles.inlineCalendar} style={style}>
      {children}
    </div>,
    document.body
  )
}

// Maps the campaign selector's source ids onto the API's per-module fields.
const MODULE_FIELD_BY_SOURCE: Record<string, keyof CustomReportModules> = {
  'ad-analysis': 'adAnalysisPageIds',
  'ai-listening': 'aiListeningCampaignIds',
  'content-analysis': 'contentAnalysisIds',
  'inbox-analysis': 'inboxPageIds',
  'review-analysis': 'reviewAnalysisCampaignIds',
  'social-listening': 'socialListeningCampaignIds',
}

interface GenerateReportModalProps {
  isOpen: boolean
  onClose: () => void
}

export const GenerateReportModal: FC<GenerateReportModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [step, setStep] = useState(1)
  const [title, setTitle] = useState('')
  const [period, setPeriod] = useState<PeriodId>('custom')
  const [fromDate, setFromDate] = useState<moment.Moment | null>(null)
  const [toDate, setToDate] = useState<moment.Moment | null>(null)
  const [openCalendar, setOpenCalendar] = useState<'from' | 'to' | null>(null)
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([])
  // Set once Generate is pressed; the wizard hides behind whichever result
  // modal is showing.
  const [status, setStatus] = useState<'error' | 'success' | null>(null)
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false)

  const campaignSources = useCampaignSources({ enabled: isOpen })

  // Measured by the campaign selector to work out how tall its list may grow.
  const modalCardRef = useRef<HTMLDivElement>(null)
  // Anchors for the portalled calendar popovers.
  const fromFieldRef = useRef<HTMLButtonElement>(null)
  const toFieldRef = useRef<HTMLButtonElement>(null)

  // Reset on open so a cancelled run never leaks into the next one.
  useEffect(() => {
    if (!isOpen) return

    setStep(1)
    setTitle('')
    setPeriod('custom')
    setFromDate(null)
    setToDate(null)
    setOpenCalendar(null)
    setSelectedCampaignIds([])
    setStatus(null)
  }, [isOpen])

  const toggleCampaign = (campaignId: string) => {
    setSelectedCampaignIds((prev) =>
      prev.includes(campaignId)
        ? prev.filter((id) => id !== campaignId)
        : [...prev, campaignId]
    )
  }

  const toggleSourceCampaigns = (campaigns: CampaignItem[]) => {
    const ids = campaigns.map(({ id }) => id)
    const areAllSelected = ids.every((id) => selectedCampaignIds.includes(id))

    setSelectedCampaignIds((prev) =>
      areAllSelected
        ? prev.filter((id) => !ids.includes(id))
        : [...prev, ...ids.filter((id) => !prev.includes(id))]
    )
  }

  const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Hard-capped rather than trusting maxLength alone, so a paste cannot
    // exceed the limit the counter promises.
    setTitle(event.target.value.slice(0, TITLE_MAX_LENGTH))
  }

  const handleSelectPeriod = (next: PeriodId) => {
    setPeriod(next)
    setOpenCalendar(null)

    const preset = PERIOD_OPTIONS.find(({ id }) => id === next)
    if (preset?.days === undefined) return

    // Presets fill the same From/To the custom range uses, so downstream only
    // ever deals with a concrete range.
    setFromDate(moment().subtract(preset.days, 'days'))
    setToDate(moment())
  }

  const handleCalendarApply = (start: moment.Moment, end: moment.Moment) => {
    if (openCalendar === 'from') setFromDate(start)
    else setToDate(end)
    setOpenCalendar(null)
  }

  const formatDate = (date: moment.Moment | null) =>
    date ? date.format('MMM D, YYYY') : ''

  const isCustom = period === 'custom'
  const hasRange = !!fromDate && !!toDate
  const isStepOne = step === 1
  const isStepTwo = step === 2
  const isStepThree = step === 3
  const isNextDisabled = isStepOne
    ? !title.trim() || !hasRange
    : !selectedCampaignIds.length

  // Sources reduced to just the picked campaigns, which is what the summary
  // renders — it does no filtering of its own.
  const selectedCampaignSources = campaignSources
    .map((source) => ({
      ...source,
      campaigns: source.campaigns.filter(({ id }) =>
        selectedCampaignIds.includes(id)
      ),
    }))
    .filter(({ campaigns }) => campaigns.length > 0)

  // Grouped by module using each campaign's `backendId` — the id that module is
  // naturally keyed on, which is NOT always the Mongo _id (inbox uses pageId,
  // ad uses page_id). Sending the selectable id would fail ownership checks.
  const buildModules = () =>
    selectedCampaignSources.reduce<Partial<CustomReportModules>>(
      (acc, source) => {
        const field = MODULE_FIELD_BY_SOURCE[source.id]
        if (field) {
          acc[field] = source.campaigns.map(({ backendId }) => backendId)
        }

        return acc
      },
      {}
    )

  const createReport = useCreateCustomReportMutation({
    onError: () => setStatus('error'),
    onSuccess: () => setStatus('success'),
  })

  return (
    <>
      <BaseModal
        contentClassName={styles.modalContent}
        // Hidden while a result or support modal is up, so the two never stack.
        // The wizard keeps its state, so closing the support modal returns the
        // user to the step they were on with their answers intact.
        isOpen={isOpen && !status && !isSupportModalOpen}
        onRequestClose={onClose}
      >
        <div className={styles.modalCard} ref={modalCardRef}>
          <CreateProjectModalHeader
            currentStep={step}
            onClose={onClose}
            title="Generate Report"
            totalSteps={TOTAL_STEPS}
          />

          {/* Step 2 reuses the selector extracted from the Projects create flow.
          The project-only props (activeSourceId, alreadyLinkedCampaignIds,
          showWarningBanner) are omitted: a report has no source lock and
          nothing is "already added". */}
          {isStepTwo && (
            <CampaignSourceSelector
              campaignSources={campaignSources}
              isOpen={isOpen}
              measureContainerRef={modalCardRef}
              onToggleCampaign={toggleCampaign}
              onToggleSourceCampaigns={toggleSourceCampaigns}
              selectedCampaignIds={selectedCampaignIds}
            />
          )}

          {isStepThree && (
            <div className={styles.reviewScrollFrame}>
              <div className={styles.reviewContent}>
                <span className={styles.reviewLabel}>Report Title</span>

                <div className={styles.reviewTitleCard}>
                  {/* Fixed glyph — unlike the project summary, whose icon is
                    derived from the chosen industries, a report has no
                    equivalent to vary on. */}
                  <span className={styles.reviewTitleIconBox}>
                    <ReportIcon className={styles.reviewTitleIcon} />
                  </span>

                  <span className={styles.reviewTitleText}>{title}</span>

                  <button
                    aria-label="Edit report title"
                    className={styles.reviewEditButton}
                    onClick={() => setStep(1)}
                    type="button"
                  >
                    <FontAwesomeIcon
                      className={styles.reviewEditIcon}
                      icon={faPen}
                    />
                  </button>
                </div>

                <span className={styles.reviewLabel}>
                  Campaigns — <strong>{selectedCampaignIds.length}</strong>{' '}
                  selected
                </span>

                <CampaignsSummary sources={selectedCampaignSources} />

                <span className={styles.reviewLabel}>Report Period</span>

                <div className={styles.rangeRow}>
                  <div className={styles.rangeField}>
                    <span className={styles.rangeLabel}>From</span>
                    <div className={styles.rangeInput}>
                      <CalendarIcon className={styles.rangeIcon} />
                      <span className={styles.rangeValue}>
                        {formatDate(fromDate)}
                      </span>
                    </div>
                  </div>

                  <div className={styles.rangeField}>
                    <span className={styles.rangeLabel}>To</span>
                    <div className={styles.rangeInput}>
                      <CalendarIcon className={styles.rangeIcon} />
                      <span className={styles.rangeValue}>
                        {formatDate(toDate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* The From/To row only exists for the custom range, so the modal is
          shorter on the presets — it sizes to its content rather than holding
          the taller custom-range height with a gap at the bottom. */}
          {isStepOne && (
            <div className={styles.body}>
              <div className={styles.titleField}>
                <span className={styles.titleLabel}>
                  Report Title <span className={styles.required}>*</span>
                </span>
                <input
                  className={styles.titleInput}
                  maxLength={TITLE_MAX_LENGTH}
                  onChange={handleTitleChange}
                  placeholder="example Hapttic Weekly Performance"
                  type="text"
                  value={title}
                />
              </div>

              <div className={styles.titleMeta}>
                <span className={styles.titleHint}>
                  Up to {TITLE_MAX_LENGTH} characters.
                </span>
                <span className={styles.titleCount}>
                  {title.length} / {TITLE_MAX_LENGTH}
                </span>
              </div>

              <div className={styles.periodSection}>
                <span className={styles.periodLabel}>Report Period</span>

                <div className={styles.periodOptions}>
                  {PERIOD_OPTIONS.map(({ id, label }) => (
                    <button
                      className={classNames(styles.periodOption, {
                        [styles.periodOptionSelected]: period === id,
                      })}
                      key={id}
                      onClick={() => handleSelectPeriod(id)}
                      type="button"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {isCustom && (
                <div className={styles.rangeRow}>
                  <div className={styles.rangeField}>
                    <span className={styles.rangeLabel}>From</span>
                    <button
                      className={classNames(styles.rangeInput, {
                        [styles.rangeInputActive]: openCalendar === 'from',
                      })}
                      onClick={() =>
                        setOpenCalendar(openCalendar === 'from' ? null : 'from')
                      }
                      ref={fromFieldRef}
                      type="button"
                    >
                      <CalendarIcon className={styles.rangeIcon} />
                      <span
                        className={classNames(styles.rangeValue, {
                          [styles.rangePlaceholder]: !fromDate,
                        })}
                      >
                        {formatDate(fromDate) || 'Select date'}
                      </span>
                    </button>

                    {openCalendar === 'from' && (
                      <CalendarPopover anchorRef={fromFieldRef}>
                        <CustomCalendar
                          initialEnd={fromDate}
                          initialStart={fromDate}
                          // Cannot start after the end of the range.
                          maxDate={toDate}
                          onApply={handleCalendarApply}
                          onClose={() => setOpenCalendar(null)}
                          onReset={() => setFromDate(null)}
                          selectionMode="single"
                        />
                      </CalendarPopover>
                    )}
                  </div>

                  <div className={styles.rangeField}>
                    <span className={styles.rangeLabel}>To</span>
                    <button
                      className={classNames(styles.rangeInput, {
                        [styles.rangeInputActive]: openCalendar === 'to',
                      })}
                      onClick={() =>
                        setOpenCalendar(openCalendar === 'to' ? null : 'to')
                      }
                      ref={toFieldRef}
                      type="button"
                    >
                      <CalendarIcon className={styles.rangeIcon} />
                      <span
                        className={classNames(styles.rangeValue, {
                          [styles.rangePlaceholder]: !toDate,
                        })}
                      >
                        {formatDate(toDate) || 'Select date'}
                      </span>
                    </button>

                    {openCalendar === 'to' && (
                      <CalendarPopover anchorRef={toFieldRef}>
                        <CustomCalendar
                          initialEnd={toDate}
                          initialStart={toDate}
                          // A report cannot cover the future, nor start before From.
                          maxDate={moment()}
                          minDate={fromDate}
                          onApply={handleCalendarApply}
                          onClose={() => setOpenCalendar(null)}
                          onReset={() => setToDate(null)}
                          selectionMode="single"
                        />
                      </CalendarPopover>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <CreateProjectModalFooter
            customLeftContent={
              <span className={styles.supportNote}>
                Custom requirements?{' '}
                <button
                  className={styles.supportLink}
                  onClick={() => setIsSupportModalOpen(true)}
                  type="button"
                >
                  contact support
                </button>
              </span>
            }
            isNextDisabled={isNextDisabled}
            leftButtonLabel={isStepOne ? 'Cancel' : 'Back'}
            onLeftButtonClick={
              isStepOne ? onClose : () => setStep((prev) => prev - 1)
            }
            onNext={() => {
              if (isStepThree) {
                // The result modal is driven by the request's outcome, via the
                // mutation's onSuccess/onError.
                createReport.mutate({
                  from: fromDate?.format(BASE_API_DATE_FORMAT) || '',
                  modules: buildModules(),
                  name: title.trim(),
                  to: toDate?.format(BASE_API_DATE_FORMAT) || '',
                })
                return
              }
              setStep((prev) => prev + 1)
            }}
            rightButtonLabel={
              // eslint-disable-next-line no-nested-ternary
              isStepThree
                ? createReport.isPending
                  ? 'Generating…'
                  : 'Generate'
                : 'Next'
            }
            showCreateIcon={isStepThree}
          />
        </div>
      </BaseModal>

      <StatusModal
        hideCountdown
        isOpen={status === 'success'}
        // Breaks pinned after "from" and "view" rather than left to the
        // container width, so the three lines hold at any size.
        message={
          <>
            Your report generation has started. You can track its progress
            anytime from
            <br />
            the <strong>Your Reports</strong> section, where you’ll be able to
            monitor its status and view
            <br />
            the report once it’s ready.
          </>
        }
        onClose={() => {
          setStatus(null)
          onClose()
        }}
        title="Report Generated Successfully"
        type="success"
      />

      {/* Try Again returns to step 3 with the wizard's answers intact, so a
          retry never makes the user re-enter anything. */}
      <StatusModal
        doneLabel="Try Again"
        hideCountdown
        isOpen={status === 'error'}
        // Break pinned after "selected" rather than left to the container
        // width.
        message={
          <>
            We couldn’t generate your report at this time. Please review your
            selected
            <br />
            filters or data range and try again in a few moments.
          </>
        }
        onClose={() => {
          setStatus(null)
          onClose()
        }}
        onSecondaryAction={() => setStatus(null)}
        secondaryLabel="Discard"
        title="Report Generation Failed"
        type="error"
      />

      <SupportContactModal
        description={
          'For custom reporting requests or specialized requirements, please ' +
          'reach out to our Support team.'
        }
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        title="Contact Support"
      />
    </>
  )
}
